import React, { Dispatch, FC, ReactNode, SetStateAction, useCallback, useState, useMemo } from 'react';
import { useWorkflowHistory, useWorkflows } from '../../../api/workflows';
import {
  Table,
  TableViewLayout,
  FlexGroup,
  Flyout,
  Code,
  FlexItem,
  Badge,
  Empty,
  Button,
  Input,
  DatePicker,
  SelectPopover,
  Loading,
  Text,
  Tooltip,
  useTableFilters,
  ComboFilterListSelector,
  ComboFilterCategory,
  ComboFilter,
} from '@lithos';
import { ExtendedTableColumn } from '@lithos/Table';
import {
  WorkflowHistoryEntry,
  WorkflowStateContainer,
  WorkflowStateContainer_BaseState,
  WorkflowType,
} from '../../../types/tecton_proto/workflows/workflow';
import { TerminalStateOptions_WorkflowTerminationCode } from '../../../types/tecton_proto/workflows/state_machine_workflow';
import IdUtils from '../../../utils/id-utils';
import { BadgeColor } from '@lithos/Badge';
import { AxiosError } from 'axios';
import { debounce, uniq } from 'lodash';
import moment from 'moment';
import { getFilteredItems } from '@lithos/TableUtils';
import { ListWorkflowsRequest } from 'src/types/tecton_proto/metadataservice/metadata_service';
import { ComboFilterProps } from '@lithos/ComboFilter';
import TimezoneContextProvider, { useTimezoneSettings } from '../../context/TimezoneContext';
import TimezoneSelector from '@shared/redesign/TimeZoneSelector';

interface WorkflowRow extends WorkflowStateContainer {
  id: string;
  state?: string;
  terminationCodeDisplayValue: string;
}

interface HistoryRow extends WorkflowHistoryEntry {
  id: string;
}

interface WorkflowFilterState {
  baseState: WorkflowStateContainer_BaseState[];
  terminationCode: string[];
}

interface FilterDetails {
  filter: ReactNode;
  activeCount: () => number;
  filterCallback: (entity: WorkflowRow) => boolean;
  name: string;
}

enum WorkflowColumnKeys {
  WORKFLOW_ID,
  BASE_STATE,
  STATE,
  TERMINATION_CODE,
  UNIQUENESS_KEY,
  ATTEMPT_INDEX,
  CREATED_AT,
  UPDATED_AT,
  COMPLETED_AT,
  LAST_EXCEPTION,
}

const DynamicTimeZoneDisplay = (value: string | undefined) => {
  const { timezone } = useTimezoneSettings();
  // TODO(UI-1377): ideally we'd use DateUtils.dateTimeFormat here, but it's not compatible with TimezoneContext right now.
  return value
    ? moment(value).tz(timezone).format('YYYY-MM-DD HH:mm:ss.SS') + ' ' + moment.tz(timezone).zoneAbbr()
    : '';
};

const workflowStateColorMap: Map<WorkflowStateContainer_BaseState, BadgeColor> = new Map([
  [WorkflowStateContainer_BaseState.BASE_STATE_UNSPECIFIED, 'gray'],
  [WorkflowStateContainer_BaseState.QUEUED, 'yellow'],
  [WorkflowStateContainer_BaseState.ACTIVE, 'blue'],
  [WorkflowStateContainer_BaseState.CANCELLING, 'orange'],
  [WorkflowStateContainer_BaseState.TERMINATED, 'blueMist'],
]);

const terminationCodeColorMap: Map<TerminalStateOptions_WorkflowTerminationCode, BadgeColor> = new Map([
  [TerminalStateOptions_WorkflowTerminationCode.OK, 'green'],
  [TerminalStateOptions_WorkflowTerminationCode.CANCELLED, 'orange'],
  [TerminalStateOptions_WorkflowTerminationCode.UNKNOWN_ERROR, 'red'],
]);

const getTerminationCodeDisplayValue = (workflow: WorkflowStateContainer): string => {
  if (workflow.termination_code != undefined) {
    return workflow.termination_code;
  }
  if (workflow.base_state === WorkflowStateContainer_BaseState.TERMINATED) {
    return '(not set)';
  }
  return '(still running)';
};

const HistoryTable: FC<{ workflowId: string }> = ({ workflowId }) => {
  const { isLoading, data: history, error } = useWorkflowHistory(workflowId);

  if (isLoading) {
    return (
      <FlexGroup justifyContent="center" direction="row" alignItems="center">
        <Loading size="xxl" />
      </FlexGroup>
    );
  }

  if (error) {
    return (
      <>
        <Empty
          placement={'embedded'}
          actions={<></>}
          useCase={'errorUnknown'}
          title={'Something Went Wrong'}
          description={
            'There seems to be a technical issue on our end. Please try again later and if this keeps happening contact us for help.'
          }
        />
      </>
    );
  }

  const historyRows: HistoryRow[] = (history?.history_entries ?? []).map((h: WorkflowHistoryEntry) => ({
    ...h,
    id: IdUtils.toStringId(h.workflow_id) ?? '(missing id)',
  }));

  if (historyRows.length == 0) {
    return <>No state change history found</>;
  }

  const historyColumns: ExtendedTableColumn<HistoryRow>[] = [
    {
      name: 'Timestamp',
      sortId: 'event_time',
      id: 'timestamp',
      content: (entry) => DynamicTimeZoneDisplay(entry.event_time),
    },
    {
      name: 'Entered State',
      columnWidth: 'xl',
      sortId: 'to_state',
      content: (entry) => <Tooltip trigger={entry.to_state} content={entry.to_state} />,
      id: 'enteredState',
    },
    {
      name: 'Reason',
      sortId: 'reason',
      columnWidth: 'xl',
      content: (entry) => <Tooltip trigger={entry.reason} content={entry.reason} />,
      id: 'reason',
    },
  ];

  return (
    <TableViewLayout
      table={<Table items={historyRows} columns={historyColumns} />}
      searchInput={undefined}
      filterAndColumnControls={undefined}
    />
  );
};

const WorkflowFlyout: FC<{ workflow: WorkflowRow; trigger: ReactNode }> = ({ workflow, trigger }) => {
  const [isFlyoutOpen, setIsFlyoutOpen] = useState<boolean>(false);

  if (workflow.workflow_id == undefined) {
    return <>{trigger}</>;
  }

  return (
    <>
      <div onClick={() => setIsFlyoutOpen(true)}>{trigger}</div>

      {isFlyoutOpen && (
        <Flyout
          title={workflow.id}
          content={<WorkflowFlyoutContent workflow={workflow} />}
          isOpen={isFlyoutOpen}
          setIsOpen={setIsFlyoutOpen}
        />
      )}
    </>
  );
};

const WorkflowFlyoutContent: FC<{ workflow: WorkflowRow }> = ({ workflow }) => {
  return (
    <FlexGroup direction="column">
      <FlexItem>
        <Text element="h3">History</Text>
      </FlexItem>
      <FlexItem>
        <HistoryTable workflowId={workflow.id} />
      </FlexItem>
      {workflow.last_exception && (
        <>
          <FlexItem>
            <Text element="h3">Last Exception</Text>
          </FlexItem>
          <FlexItem>
            <Code
              language="javascript"
              hideLineNumbers={true}
              code={`${workflow.last_exception.name || 'Unknown Exception'}: ${
                workflow.last_exception.message || 'No message'
              }\n\n${workflow.last_exception.stack_trace_to_string || 'No stack trace'}`}
            />
          </FlexItem>
        </>
      )}
      <FlexItem>
        <Text element="h3">Last Known State Container</Text>
      </FlexItem>
      <FlexItem>
        <Code
          language="json"
          hideLineNumbers={true}
          code={JSON.stringify(workflow as WorkflowStateContainer, null, 2)}
        />
      </FlexItem>
    </FlexGroup>
  );
};

const IdCell: FC<{ workflow: WorkflowRow }> = ({ workflow }) => {
  if (workflow.workflow_id == undefined) {
    return (
      <Empty
        placement={'embedded'}
        actions={<></>}
        useCase={'errorKnown'}
        title={'Missing Workflow ID'}
        description={
          'Somehow this workflow lacks a Workflow ID. This should not be possible. Please reach out for help.'
        }
      />
    );
  }

  return (
    <WorkflowFlyout workflow={workflow} trigger={<Button label={<Text code>{workflow.id}</Text>} type="secondary" />} />
  );
};

const DebouncedSearchInput: FC<{
  label: string;
  setQuery: (value: string) => void;
}> = ({ label, setQuery }) => {
  const [realTimeValue, setRealTimeValue] = useState('');

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceValue = useCallback(
    debounce((val: string) => {
      setQuery(val);
    }, 300),
    [setQuery]
  );

  return (
    <Input
      value={realTimeValue}
      type="string"
      label={label}
      icon="Search"
      onChange={(value: string) => {
        setRealTimeValue(value);
        debounceValue(value);
      }}
    />
  );
};

const unfilterable: FilterDetails = {
  filter: <></>,
  activeCount: () => 0,
  filterCallback: () => true,
  name: '',
};

const ComboFilterWithManagedCategory: FC<Omit<ComboFilterProps, 'selectedCategory' | 'setSelectedCategory'>> = (
  props
) => {
  const [selectedCategory, setSelectedCategory] = useState<ComboFilterCategory | undefined>(undefined);
  return <ComboFilter {...props} selectedCategory={selectedCategory} setSelectedCategory={setSelectedCategory} />;
};

const WorkflowsTable: FC<{
  workflowRows: WorkflowRow[];
  filterComponents: Record<WorkflowColumnKeys, FilterDetails>;
  setQueryUniquenessKey: (value: string) => void;
}> = ({ workflowRows, filterComponents, setQueryUniquenessKey }) => {
  const workflowColumns: ExtendedTableColumn<WorkflowRow>[] = [
    {
      name: 'Updated At',
      id: WorkflowColumnKeys.UPDATED_AT,
      content: (value: WorkflowRow) => DynamicTimeZoneDisplay(value.updated_at),
    },
    {
      name: 'Workflow ID',
      id: WorkflowColumnKeys.WORKFLOW_ID,
      columnWidth: 'xl',
      content: (workflow) => <IdCell workflow={workflow} />,
    },
    { name: 'Type', sortId: 'workflow_type', columnWidth: 'xl', content: (value: WorkflowRow) => value.workflow_type },
    {
      name: 'Base State',
      id: WorkflowColumnKeys.BASE_STATE,
      columnWidth: 'm',
      content: (value: WorkflowRow) => {
        if (value.base_state != undefined) {
          return <Badge label={value.base_state} color={workflowStateColorMap.get(value.base_state)} />;
        }
      },
      filterContent: filterComponents[WorkflowColumnKeys.BASE_STATE].filter,
    },
    {
      name: 'State',
      id: WorkflowColumnKeys.STATE,
      columnWidth: 'xl',
      content: (value: WorkflowRow) => <Tooltip trigger={value.state} content={value.state} />,
    },
    {
      name: 'Termination Code',
      id: WorkflowColumnKeys.TERMINATION_CODE,
      columnWidth: 'm',
      content: (value: WorkflowRow) => {
        if (value.termination_code != undefined) {
          return (
            <Badge
              label={value.terminationCodeDisplayValue}
              color={terminationCodeColorMap.get(value.termination_code) ?? 'red'}
            />
          );
        }
        if (value.base_state === WorkflowStateContainer_BaseState.TERMINATED) {
          return <Badge label={value.terminationCodeDisplayValue} color="gray" />;
        }
      },
      filterContent: filterComponents[WorkflowColumnKeys.TERMINATION_CODE].filter,
    },
    {
      name: 'Exception?',
      id: WorkflowColumnKeys.LAST_EXCEPTION,
      columnWidth: 's',
      content: (value: WorkflowRow) => {
        if (value.last_exception) {
          return (
            <WorkflowFlyout
              workflow={value}
              trigger={
                <Tooltip
                  trigger={<Button icon="Warning" type="ghost" />}
                  content="Click to view workflow details and exception"
                />
              }
            />
          );
        }
        return null;
      },
    },
    {
      name: 'Uniqueness Key',
      id: WorkflowColumnKeys.UNIQUENESS_KEY,
      columnWidth: 'xl',
      content: (value) => (
        <Tooltip
          trigger={
            <Button
              label={<Text code>{value.uniqueness_key}</Text>}
              type="ghost"
              onClick={() => {
                setQueryUniquenessKey(value.uniqueness_key ?? '');
              }}
            />
          }
          content={value.uniqueness_key}
        />
      ),
    },
    {
      name: 'Attempt Index',
      id: WorkflowColumnKeys.ATTEMPT_INDEX,
      columnWidth: 's',
      content: (value: WorkflowRow) => value.attempt_index,
    },
    {
      name: 'Created At',
      id: WorkflowColumnKeys.CREATED_AT,
      content: (value: WorkflowRow) => DynamicTimeZoneDisplay(value.created_at),
    },
    {
      name: 'Completed At',
      id: WorkflowColumnKeys.COMPLETED_AT,
      content: (value: WorkflowRow) => DynamicTimeZoneDisplay(value.completed_at),
    },
  ];

  return <Table items={workflowRows} columns={workflowColumns} />;
};

const WorkflowsTableView: FC<{
  workflowRows: WorkflowRow[];
  workflowsQuery: ListWorkflowsRequest;
  setWorkflowsQuery: Dispatch<SetStateAction<ListWorkflowsRequest>>;
  isStale: boolean;
}> = ({ workflowRows, workflowsQuery, setWorkflowsQuery, isStale }) => {
  const [workflowTypeSelectionOpen, setWorkflowTypeSelectionOpen] = useState(false);

  // Set up in-memory filters.
  const defaultFilterState = {
    baseState: [],
    terminationCode: [],
  };
  const { hasAnyFilters, activeFilterCount, filterState, handleResetFilters, handleFilterChange } = useTableFilters<
    WorkflowRow,
    WorkflowColumnKeys,
    WorkflowFilterState
  >(defaultFilterState);

  const filterComponents: Record<WorkflowColumnKeys, FilterDetails> = useMemo(() => {
    return {
      [WorkflowColumnKeys.TERMINATION_CODE]: {
        filter: (
          <ComboFilterListSelector
            title={'Termination Code'}
            selected={filterState.terminationCode}
            setSelected={(state) => handleFilterChange(filterComponents, 'terminationCode', state)}
            items={uniq(workflowRows?.map((item) => item.terminationCodeDisplayValue)).sort()}
          />
        ),
        activeCount: () => filterState.terminationCode.length,
        filterCallback: (workflow) => filterState.terminationCode.includes(workflow.terminationCodeDisplayValue),
        name: 'Termination Code',
      },
      [WorkflowColumnKeys.BASE_STATE]: {
        filter: (
          <ComboFilterListSelector
            title={'Base State'}
            selected={filterState.baseState}
            setSelected={(state) => handleFilterChange(filterComponents, 'baseState', state)}
            items={uniq(workflowRows?.map((item) => item.base_state ?? '')).sort()}
          />
        ),
        activeCount: () => filterState.baseState.length,
        filterCallback: (workflow) =>
          filterState.baseState.includes(
            workflow.base_state ?? WorkflowStateContainer_BaseState.BASE_STATE_UNSPECIFIED
          ),
        name: 'Base State',
      },
      [WorkflowColumnKeys.WORKFLOW_ID]: unfilterable,
      [WorkflowColumnKeys.UNIQUENESS_KEY]: unfilterable,
      [WorkflowColumnKeys.UPDATED_AT]: unfilterable,
      [WorkflowColumnKeys.COMPLETED_AT]: unfilterable,
      [WorkflowColumnKeys.CREATED_AT]: unfilterable,
      [WorkflowColumnKeys.STATE]: unfilterable,
      [WorkflowColumnKeys.ATTEMPT_INDEX]: unfilterable,
      [WorkflowColumnKeys.LAST_EXCEPTION]: unfilterable,
    };
  }, [workflowRows, filterState, handleFilterChange]);

  // These define the filter categories in the Filter button popover
  const categories: ComboFilterCategory[] = [
    {
      name: filterComponents[WorkflowColumnKeys.BASE_STATE].name,
      resetCallback: () => {
        handleFilterChange(filterComponents, 'baseState', defaultFilterState.baseState);
      },
      icon: 'Tag',
      activeCount: filterComponents[WorkflowColumnKeys.BASE_STATE].activeCount(),
      popover: filterComponents[WorkflowColumnKeys.BASE_STATE].filter,
      disabled: false,
    },
    {
      name: filterComponents[WorkflowColumnKeys.TERMINATION_CODE].name,
      resetCallback: () => {
        handleFilterChange(filterComponents, 'baseState', defaultFilterState.baseState);
      },
      icon: 'Tag',
      activeCount: filterComponents[WorkflowColumnKeys.TERMINATION_CODE].activeCount(),
      popover: filterComponents[WorkflowColumnKeys.TERMINATION_CODE].filter,
      disabled: false,
    },
  ];
  const activeFilters = Object.values(filterComponents).filter((filter) => {
    return filter.activeCount() !== 0;
  });
  const filteredResults = getFilteredItems(workflowRows, activeFilters, ['base_state'], '', undefined);
  return (
    <TimezoneContextProvider>
      <TableViewLayout
        table={
          <WorkflowsTable
            workflowRows={filteredResults}
            filterComponents={filterComponents}
            setQueryUniquenessKey={(value: string) =>
              setWorkflowsQuery({
                ...workflowsQuery,
                partial_uniqueness_key: value,
              })
            }
          />
        }
        searchInput={
          <FlexGroup direction="row">
            <FlexItem>
              <DebouncedSearchInput
                label="ID"
                setQuery={(value: string) =>
                  setWorkflowsQuery({
                    ...workflowsQuery,
                    partial_workflow_id: value,
                  })
                }
              />
            </FlexItem>
            <FlexItem>
              <DebouncedSearchInput
                label="Uniqueness Key"
                setQuery={(value: string) =>
                  setWorkflowsQuery({
                    ...workflowsQuery,
                    partial_uniqueness_key: value,
                  })
                }
              />
            </FlexItem>
            <FlexItem>
              <FlexGroup justifyContent="center" direction="column">
                <SelectPopover
                  iconLeft="Search"
                  label={workflowsQuery.type ? workflowsQuery.type : 'Type'}
                  filterable={true}
                  groups={[
                    {
                      items: Object.values(WorkflowType).map((val) => {
                        return {
                          name: val,
                          selectable: true,
                          selected: workflowsQuery.type == val,
                          onClick: () => {
                            setWorkflowsQuery({
                              ...workflowsQuery,
                              type: workflowsQuery.type == val ? undefined : val,
                            });
                            setWorkflowTypeSelectionOpen(false);
                          },
                        };
                      }),
                    },
                  ]}
                  isOpen={workflowTypeSelectionOpen}
                  setIsOpen={setWorkflowTypeSelectionOpen}
                />
              </FlexGroup>
            </FlexItem>
            <FlexItem>
              <DatePicker
                // TODO(UI-1374): use timeZoneContext in pickers, remove awkward headers for local/display time.
                label="From (local time)"
                currentDate={
                  workflowsQuery.updated_within?.start ? new Date(workflowsQuery.updated_within.start) : new Date()
                }
                setCurrentDate={(time) => {
                  const end = workflowsQuery.updated_within?.end;
                  setWorkflowsQuery({
                    ...workflowsQuery,
                    updated_within: {
                      start: time?.toISOString(),
                      end: time && end && time > new Date(end) ? moment(time).add(1, 'day').toISOString() : end,
                    },
                  });
                }}
                allowTimeSelection={true}
              />
            </FlexItem>
            <FlexItem>
              <DatePicker
                label="To (local time)"
                currentDate={
                  workflowsQuery.updated_within?.end ? new Date(workflowsQuery.updated_within.end) : new Date()
                }
                setCurrentDate={(time) => {
                  const start = workflowsQuery.updated_within?.start;
                  setWorkflowsQuery({
                    ...workflowsQuery,
                    updated_within: {
                      start:
                        time && start && time < new Date(start) ? moment(time).subtract(1, 'day').toISOString() : start,
                      end: time?.toISOString(),
                    },
                  });
                }}
                allowTimeSelection={true}
              />
            </FlexItem>
            <FlexItem>
              {isStale && (
                <FlexGroup justifyContent="center" direction="column">
                  <Loading size="l" />
                </FlexGroup>
              )}
            </FlexItem>
          </FlexGroup>
        }
        filterAndColumnControls={
          <FlexGroup direction="row">
            <FlexItem>
              <FlexGroup direction="column">
                <TimezoneSelector />
                <Text size="xs">Display Timezone</Text>
              </FlexGroup>
            </FlexItem>
            <FlexItem>
              {' '}
              <ComboFilterWithManagedCategory
                icon={'Filters'}
                hasAnyActiveFilters={hasAnyFilters}
                activeFilterCount={activeFilterCount}
                resetFilters={handleResetFilters}
                categories={categories}
              />
            </FlexItem>{' '}
          </FlexGroup>
        }
      />
    </TimezoneContextProvider>
  );
};

const WorkflowsDebug = () => {
  const [workflowsQuery, setWorkflowsQuery] = useState<ListWorkflowsRequest>({
    updated_within: {
      start: moment().subtract(14, 'days').toISOString(),
      end: moment().toISOString(),
    },
  });
  const { data, error, isStale } = useWorkflows(workflowsQuery, /* keepPreviousData */ true);

  if (error) {
    if ((error as AxiosError).response?.status == 500) {
      return (
        <Empty
          placement={'full-page'}
          actions={<></>}
          useCase={'permissions'}
          title={'No Access'}
          description={'Looks like you don’t have access to this tool, it is for Tecton internal use only.'}
        />
      );
    } else {
      return (
        <Empty
          placement={'full-page'}
          actions={<></>}
          useCase={'errorUnknown'}
          title={'Something Went Wrong'}
          description={
            'There seems to be a technical issue on our end. Please try again later and if this keeps happening contact us for help.'
          }
        />
      );
    }
  } else if (data == undefined) {
    // Should only be the first load since we have keepPreviousData set to true.
    return (
      <FlexGroup justifyContent="center" direction="row" alignItems="center">
        <Loading size="xxl" />
      </FlexGroup>
    );
  }

  return (
    <WorkflowsTableView
      isStale={isStale}
      workflowsQuery={workflowsQuery}
      setWorkflowsQuery={setWorkflowsQuery}
      workflowRows={(data.workflows ?? []).map((w: WorkflowStateContainer) => ({
        ...w,
        id: IdUtils.toStringId(w.workflow_id) ?? '(missing id)',
        state: data.workflow_state_names?.[IdUtils.toStringId(w.workflow_id)] ?? '',
        terminationCodeDisplayValue: getTerminationCodeDisplayValue(w),
      }))}
    />
  );
};

export default WorkflowsDebug;
